import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";
import { ModalConfirm } from "tamtam-components";
import { toast } from "react-toastify";

import Loader from "../../common/Loader";
import Tabs from "../../common/Tabs";
import _ from "../../../i18n";
import {
  resendInvitation,
  getExpressUser,
  deleteExpressUser,
  dessociateGoPeopleUser,
  getGoPeopleCollaborators,
  getGoPeopleTmpCollaborators,
  searchGoPeopleCollaboratorsOrganization,
} from "../../../api";
import { formatPhone, formatHidePhone } from "../../../utils";
import styles from "./Collaborator.module.scss";
import Pagination from "../../common/Pagination";
import { StatusButton } from "../../";
import UserRoleModal from "../Contact/UserRoleModal";

const CollaboratorList = (props) => {
  const prevSearchRef = useRef();
  prevSearchRef.current = "";
  const queryClient = useQueryClient();
  const auth = useSelector((state) => state.auth);
  const language = useSelector((state) => state.params.language);
  const fiduciary = useSelector(
    (state) => state.auth.navCommunity.selectedOrganization
  );
  const [sort, setSort] = useState({
    property: "lastName",
    dir: "asc",
  });
  const [dropDownId, setDropDownId] = useState(null);
  const [search, setSearch] = useState("");
  const [foundResult, setFoundResult] = useState([]);
  const [totalFoundResult, setTotalFoundResult] = useState([]);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentDeleteItem, setCurrentDeleteItem] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCollaborators, setTotalCollaborators] = useState(0);
  const [totalExpress, setTotalExpress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [totalTmpCollaborators, setTotalTmpCollaborators] = useState(0);
  const [totalRejectedExpress, setTotalRejectedExpress] = useState(0);
  const [totalRejectedTmpCollaborators, setTotalRejectedTmpCollaborators] =
    useState(0);
  const [dataGoPeople, setDataGoPeople] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [userRoleModal, setUserRoleModal] = useState(null);
  const { token, jwtToken, goPeopleOrganizationId, goPeopleCommunityId } = auth;
  const LIMIT = 10;

  useEffect(() => {
    props.setNbCollaborators(
      totalCollaborators + totalExpress + totalTmpCollaborators
    );
  }, [totalCollaborators, totalExpress, totalTmpCollaborators]);

  let { isLoading } = useQuery(
    ["listCollaborators", token, sort, currentPage, goPeopleOrganizationId],
    async () => {
      if (token && fiduciary && search === "") {
        const response = await getGoPeopleCollaborators(
          goPeopleOrganizationId,
          LIMIT,
          currentPage
        );
        setTotalCollaborators(response.data.total_count);
        setDataGoPeople(prepareDataGoPeople(response.data));
        return {
          data: response.data.users,
          nbResult: response.data.total_count ?? 0,
        };
      }
    }
  );

  let listExpress = useQuery(["listExpress", token, fiduciary], async () => {
    if (token && fiduciary) {
      let response = await getExpressUser({
        token,
        status: "PENDING,IN_PROGRESS",
        fiduciaryId: fiduciary.id,
      });
      if (response?.data?.data) {
        setTotalExpress(response.data.data.length);
      } else {
        setTotalExpress(0);
      }

      return response.data;
    }
  });

  let listTmpCollaborators = useQuery(
    ["listTmpCollaborators", token],
    async () => {
      if (token && fiduciary) {
        try {
          let response = await getGoPeopleTmpCollaborators(
            goPeopleOrganizationId,
            "PENDING"
          );
          if (response.total_count) {
            setTotalTmpCollaborators(response.total_count);
          } else {
            setTotalTmpCollaborators(0);
          }
          return prepareDataGoPeople(response);
        } catch (e) {
          setTotalTmpCollaborators(0);
          return [];
        }
      }
    }
  );

  let listRejectedExpress = useQuery(
    ["listRejectedExpress", token, fiduciary],
    async () => {
      if (token && fiduciary) {
        let response = await getExpressUser({
          token,
          status: "REJECTED",
          fiduciaryId: fiduciary.id,
        });
        if (response?.data?.data) {
          setTotalRejectedExpress(response.data.data.length);
        } else {
          setTotalRejectedExpress(0);
        }

        return response.data;
      }
    }
  );

  let listRejectedTmpCollaborators = useQuery(
    ["listRejectedTmpCollaborators", token],
    async () => {
      if (token && fiduciary) {
        try {
          let response = await getGoPeopleTmpCollaborators(
            goPeopleOrganizationId,
            "REJECTED"
          );
          if (response?.data?.total_count) {
            setTotalRejectedTmpCollaborators(response.data?.total_count);
          } else {
            setTotalRejectedTmpCollaborators(0);
          }
          return response.data.users;
        } catch (e) {
          setTotalRejectedTmpCollaborators(0);
          return [];
        }
      }
    }
  );

  useEffect(() => {
    if (search !== "") {
      handleSearchChange(currentPage, search);
    }
  }, [currentPage]);

  useEffect(() => {
    if (prevSearchRef.current === "") {
      setCurrentPage(1);
    }
  }, [search]);

  useEffect(() => {
    if (props.refreshList) {
      queryClient.invalidateQueries("listCollaborators");
      queryClient.invalidateQueries("listExpress");
      queryClient.invalidateQueries("listTmpCollaborators");
      props.setRefreshList(false);
    }
  }, [props.refreshList, goPeopleOrganizationId, goPeopleCommunityId]);

  const toggleDropDown = (e, userId) => {
    e.stopPropagation();
    dropDownId === userId ? setDropDownId(null) : setDropDownId(userId);
  };

  const prepareDataGoPeople = (data) => {
    return data && data.users && data.users.length > 0
      ? data.users.map((item) => {
          let role = data.roles?.filter(
            (cont) => cont.user_id === item.uuid
          )[0];
          return { ...item, role };
        })
      : [];
  };

  const prepareFoundDataGoPeople = (data) => {
    return data
      ? data.map((item) => {
          let role = item.roles ? item.roles[0] : null;
          return { ...item, role };
        })
      : [];
  };

  const handleSearchChange = async (page, value) => {
    setSearch(value);
    prevSearchRef.current = value;
    if (value === "") {
      return;
    }
    setUploading(true);
    const response = await searchGoPeopleCollaboratorsOrganization(
      jwtToken,
      goPeopleCommunityId,
      goPeopleOrganizationId,
      value,
      page - 1
    );
    setFoundResult(prepareFoundDataGoPeople(response.results));
    setTotalFoundResult(response.total);
    setUploading(false);
  };

  const handleDeleteAction = () => {
    if (currentDeleteItem) {
      setDeleting(true);
      if (currentDeleteItem.itemType === "CLASSIC" && currentDeleteItem.role) {
        dessociateGoPeopleUser(
          goPeopleOrganizationId,
          currentDeleteItem.role?.uuid
        )
          .then((resp) => {
            if (search !== "") {
              handleSearchChange(currentPage, search);
            } else {
              queryClient.invalidateQueries("listCollaborators");
            }
            setTimeout(() => {
              toast.success(_("successfully_deleted"));
              setDeleting(false);
              setIsOpenDeleteModal(false);
              props.afterDelete();
            }, 1000);
          })
          .catch((e) => {
            setDeleting(false);
          });
      } else if (currentDeleteItem.itemType === "EXPRESS") {
        deleteExpressUser(auth.token, currentDeleteItem.id)
          .then((resp) => {
            queryClient.invalidateQueries("listExpress");
            setTimeout(() => {
              toast.success(_("successfully_deleted"));
              setDeleting(false);
              setIsOpenDeleteModal(false);
              props.afterDelete();
            }, 1000);
          })
          .catch((e) => {
            setDeleting(false);
          });
      } else if (currentDeleteItem.itemType === "TMP") {
        // deleteGoPeopleUser(currentDeleteItem.uuid)
        dessociateGoPeopleUser(
          goPeopleOrganizationId,
          currentDeleteItem.role?.uuid
        )
          .then((resp) => {
            queryClient.invalidateQueries("listTmpCollaborators");
            setTimeout(() => {
              toast.success(_("successfully_deleted"));
              setDeleting(false);
              setIsOpenDeleteModal(false);
              props.afterDelete();
            }, 1000);
          })
          .catch((e) => {
            setDeleting(false);
          });
      }
    }
  };

  const getItemActions = (item, itemType) => {
    if (auth.user.role === "OFFICIAL" || auth.user.role === "EXTERNAL") {
      return null;
    }

    return (
      <div className={styles.dropDownContainer}>
        <span
          className={`${styles.cellIcon} ${
            dropDownId === item.uuid && styles.active
          }`}
        >
          <span
            className={`${
              dropDownId === item.uuid
                ? "icon-ttp-close"
                : "icon-ttp-three-dots"
            } ${styles.settingsIcon}`}
            onClick={(e) => toggleDropDown(e, item.uuid)}
          />
        </span>
        <ul
          className={`${styles.menuDropdown} ${
            dropDownId === item.uuid && styles.show
          }`}
        >
          {(!["LEGAL_REPRESENTATIVE", "MANAGER"].includes(auth.user.role) ||
            "ADMIN" === auth.user.type) && (
            <li
              onClick={() => {
                setCurrentDeleteItem({ ...item, itemType });
                setIsOpenDeleteModal(true);
              }}
            >
              {_("delete")}
            </li>
          )}
        </ul>
      </div>
    );
  };

  const getActions = (item) => {
    if (
      auth.user.role === "OFFICIAL" ||
      auth.user.role === "EXTERNAL" ||
      ((auth.user.role === "MANAGER" ||
        auth.user.role == "FIDUCIARIES_ADMIN") &&
        item.role.role === "LEGAL_REPRESENTATIVE")
    ) {
      return null;
    }

    if (
      "LEGAL_REPRESENTATIVE" === auth.user.role &&
      "ADMIN" === auth.user.role.type_status &&
      "LEGAL_REPRESENTATIVE" === item.role.role &&
      auth.user.id !== item.id
    ) {
      return null;
    }
    if (item?.role?.exist && item.uaStatus === "PENDING") {
      return null;
    }

    return (
      <div className={styles.dropDownContainer}>
        <span
          className={`${styles.cellIcon} ${
            dropDownId === item.uuid && styles.active
          }`}
        >
          <span
            className={`${
              dropDownId === item.uuid
                ? "icon-ttp-close"
                : "icon-ttp-three-dots"
            } ${styles.settingsIcon}`}
            onClick={(e) => toggleDropDown(e, item.uuid)}
          />
        </span>
        <ul
          className={`${styles.menuDropdown} ${
            dropDownId === item.uuid && styles.show
          }`}
        >
          <li
            onClick={(e) => {
              toggleDropDown(e, item.uuid);
              props.setSideBar && props.setSideBar("COLLABORATOR_FORM");
              props.setCurrentItem(item);
            }}
          >
            {_("edit")}
          </li>

          <li
            onClick={(e) => {
              toggleDropDown(e, item.uuid);
              setUserRoleModal(item);
            }}
          >
            {_("view_delegations_roles")}
          </li>

          {(!["LEGAL_REPRESENTATIVE", "MANAGER"].includes(auth.user.role) ||
            "ADMIN" === auth.user.type) &&
            auth.user.id !== item.id && (
              <li
                onClick={() => {
                  setCurrentDeleteItem({ ...item, itemType: "CLASSIC" });
                  setIsOpenDeleteModal(true);
                }}
              >
                {_("delete")}
              </li>
            )}
        </ul>
      </div>
    );
  };

  const handleClick = (type, id) => {
    setIsSending(true);
    let data = { token, language, fid: fiduciary.id };
    type === "uid" ? (data.uid = id) : (data.id = id);
    resendInvitation({
      communityId: goPeopleCommunityId,
      organizationId: goPeopleOrganizationId,
      userId: id,
      lng: language,
    })
      .then(() => {
        toast.success(_("reminder_email_sent_successfully"));
      })
      .catch((e) => {
        if (
          e.response &&
          e.response.data &&
          e.response.data.errors &&
          e.response.data.errors.length > 0
        )
          toast.error(_(e.response.data.errors[0].message));
        else toast.error(_("error"));
      })

      .finally(() => setIsSending(false));
  };

  const renderPendingData = () => {
    let expressData =
      listExpress?.data && listExpress.data?.data?.length > 0
        ? listExpress.data.data.map((item) => (
            <tr key={item.uuid}>
              <td>{getItemActions(item, "EXPRESS")}</td>
              <td>{`${item?.user?.lastName ? item.user.lastName : "-"} ${
                item?.user?.firstName ? item.user.firstName : ""
              }`}</td>
              <td>{item.email}</td>
              <td className={styles.nowrap}>
                {item?.user?.mainPhone
                  ? formatHidePhone(item.user.mainPhone)
                  : "-"}
              </td>
              <td>{_("OFFICIAL")}</td>
              <td>-</td>
              <td>
                <StatusButton
                  status={item.status}
                  handleClick={() => handleClick("id", item.id)}
                />
              </td>
            </tr>
          ))
        : [];

    let tmpCollabData =
      listTmpCollaborators?.data && listTmpCollaborators?.data.length > 0
        ? listTmpCollaborators.data.map((item) => (
            <tr key={item.uuid}>
              <td>{getItemActions(item, "TMP")}</td>
              <td>{`${item?.lastname ? item.lastname : "-"} ${
                item?.firstname ? item.firstname : ""
              }`}</td>
              <td>{item.email}</td>
              <td className={styles.nowrap}>
                {item?.phone ? formatHidePhone(item.phone) : "-"}
              </td>
              <td>
                {fiduciary.uaMode === "UNIVERSITY" && item.type === "OFFICIAL"
                  ? _("student")
                  : _(item.role?.role)}
              </td>
              <td>{item.main_language}</td>
              <td>
                <StatusButton
                  status={item.role.status}
                  handleClick={() => handleClick("uid", item.uuid)}
                />
              </td>
            </tr>
          ))
        : [];
    if (expressData?.length == 0 && tmpCollabData?.length == 0) {
      return (
        <tr>
          <td colSpan={6} className={styles.centerCell}>
            {_("empty_list")}
          </td>
        </tr>
      );
    }

    return [...expressData, ...tmpCollabData];
  };

  const renderData = (data) => {
    let collabData =
      data && data.length > 0
        ? data.map((item) => (
            <tr key={item.uuid}>
              <td>{getActions(item)}</td>
              <td>{`${item.lastname} ${item.firstname}`}</td>
              <td>{item.email}</td>
              <td className={styles.nowrap}>
                {item.phone && formatPhone(item.phone)}
              </td>
              <td>
                {fiduciary &&
                fiduciary.uaMode === "UNIVERSITY" &&
                item.role?.role === "OFFICIAL"
                  ? _("student")
                  : ["LEGAL_REPRESENTATIVE", "MANAGER"].includes(
                        item.role?.role
                      )
                    ? "Admin"
                    : _(item.role?.role)}
              </td>
              <td>{item.main_language}</td>
              <td>
                <StatusButton
                  status={"VALIDATED"}
                  handleClick={() => handleClick("uid", item.uuid)}
                />
              </td>
            </tr>
          ))
        : [];

    if (collabData?.length == 0) {
      return (
        <tr>
          <td colSpan={6} className={styles.centerCell}>
            {_("empty_list")}
          </td>
        </tr>
      );
    }

    return collabData;
  };

  const renderRejectedData = () => {
    let expressData =
      listRejectedExpress?.data && listRejectedExpress.data?.data?.length > 0
        ? listRejectedExpress.data.data.map((item) => (
            <tr key={item.id}>
              <td>{getItemActions(item, "EXPRESS")}</td>
              <td>{`${item?.user?.lastName ? item.user.lastName : "-"} ${
                item?.user?.firstName ? item.user.firstName : ""
              }`}</td>
              <td>{item.email}</td>
              <td className={styles.nowrap}>
                {item?.user?.mainPhone
                  ? formatHidePhone(item.user.mainPhone)
                  : "-"}
              </td>
              <td>{_("OFFICIAL")}</td>
              <td>-</td>
              <td>
                <StatusButton
                  status={item.status}
                  handleClick={() => handleClick("id", item.id)}
                />
              </td>
            </tr>
          ))
        : [];

    let tmpCollabData =
      listRejectedTmpCollaborators?.data &&
      listRejectedTmpCollaborators.data?.length > 0
        ? listRejectedTmpCollaborators.data.map((item) => (
            <tr key={item.id}>
              <td>{getItemActions(item, "TMP")}</td>
              <td>{`${item?.lastname ? item.lastname : "-"} ${
                item?.firstname ? item.firstname : ""
              }`}</td>
              <td>{item.email}</td>
              <td className={styles.nowrap}>
                {item?.phone ? formatHidePhone(item.phone) : "-"}
              </td>
              <td>{_(item.role.role)}</td>
              <td>{item.main_language}</td>
              <td>
                <StatusButton
                  status={item.role.status}
                  handleClick={() => handleClick("uid", item.uuid)}
                />
              </td>
            </tr>
          ))
        : [];
    if (expressData?.length == 0 && tmpCollabData?.length == 0) {
      return (
        <tr>
          <td colSpan={6} className={styles.centerCell}>
            {_("empty_list")}
          </td>
        </tr>
      );
    }

    return [...expressData, ...tmpCollabData];
  };

  return (
    <>
      {isSending && <div className="lmask"></div>}
      <div className={styles.listContainer}>
        <h3>
          {fiduciary && fiduciary.uaMode === "UNIVERSITY"
            ? _("students_list")
            : _("collaborators_list")}{" "}
          {search !== "" ? (
            <span>({totalFoundResult})</span>
          ) : (
            !isLoading &&
            !listExpress.isLoading &&
            !listTmpCollaborators.isLoading && (
              <span>
                ({totalCollaborators + totalExpress + totalTmpCollaborators})
              </span>
            )
          )}
        </h3>
        <div className={styles.inputSearch}>
          <span className={styles.inputSearch_icon}>
            <i className="icon-ttp-magnifier"></i>
          </span>
          <input
            type="text"
            id="search-go-collab"
            autoComplete="off"
            placeholder="Recherche ..."
            onChange={(e) => handleSearchChange(1, e.target.value)}
          />
          {search !== "" && (
            <span
              className={styles.inputSearch_close}
              onClick={() => {
                setSearch("");
                prevSearchRef.current = "";
                document.getElementById("search-go-collab").value = "";
              }}
            >
              <i className="icon-ttp-close"></i>
            </span>
          )}
        </div>
      </div>
      <div className={styles.tabContainer}>
        {props.page && props.page === "SETTING" && (
          <ul className={styles.tabForms}>
            <li
              onClick={() => {
                props.setCurrentItem(null);
                props.setSideBar("COLLABORATOR_FORM");
              }}
              className={
                props.sideBar === "COLLABORATOR_FORM" ? styles.active : ""
              }
            >
              Ajout Manuel
            </li>
            <li
              onClick={() => {
                props.setCurrentItem(null);
                props.setSideBar("COLLABORATOR_IMPORT");
              }}
              className={
                props.sideBar === "COLLABORATOR_IMPORT" ? styles.active : ""
              }
            >
              Import (.xls)
            </li>
            {fiduciary?.uaMode !== "UNIVERSITY" && (
              <li
                onClick={() => {
                  props.setCurrentItem(null);
                  props.setSideBar("COLLABORATOR_EXPRESS");
                }}
                className={
                  props.sideBar === "COLLABORATOR_EXPRESS" ? styles.active : ""
                }
              >
                Ajout Express
              </li>
            )}
          </ul>
        )}
      </div>

      <Tabs theme="wtheme">
        <div key={`acitive`} label={_("validated")}>
          <div className={styles.tableContainer}>
            <table
              className={`${styles.table} unstriped`}
              onClick={() => setDropDownId(null)}
            >
              <thead>
                <tr>
                  <th></th>
                  <th width="30%" className={styles.nowrap}>
                    <span>{_("lastname") + " / " + _("firstname")}</span>
                    <span className={styles.sortBox}>
                      <i
                        className={`icon-ttp-arrow-down ${
                          sort.property === "lastName" &&
                          sort.dir === "asc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "lastName", dir: "asc" })
                        }
                      ></i>
                      <i
                        className={`icon-ttp-arrow-up ${
                          sort.property === "lastName" &&
                          sort.dir === "desc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "lastName", dir: "desc" })
                        }
                      ></i>
                    </span>
                  </th>
                  <th width="30%">
                    <span>Email</span>
                    <span className={styles.sortBox}>
                      <i
                        className={`icon-ttp-arrow-down ${
                          sort.property === "email" &&
                          sort.dir === "asc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "email", dir: "asc" })
                        }
                      ></i>
                      <i
                        className={`icon-ttp-arrow-up ${
                          sort.property === "email" &&
                          sort.dir === "desc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "email", dir: "desc" })
                        }
                      ></i>
                    </span>
                  </th>
                  <th width="25%">{_("phone_number")}</th>
                  <th width="25%">{_("role")}</th>
                  <th>{_("language")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {uploading ||
                isLoading ||
                listExpress.isLoading ||
                listTmpCollaborators.isLoading ? (
                  <tr>
                    <td colSpan={6} className={styles.centerCell}>
                      <Loader
                        style={{
                          height: "10px",
                        }}
                        color={"#B2BCC6"}
                      />
                    </td>
                  </tr>
                ) : search !== "" ? (
                  renderData(foundResult)
                ) : (
                  renderData(dataGoPeople)
                )}
              </tbody>
            </table>
          </div>
          {totalCollaborators ? (
            <Pagination
              className="pagination-bar"
              currentPage={currentPage}
              totalCount={search !== "" ? totalFoundResult : totalCollaborators}
              pageSize={LIMIT}
              onPageChange={(page) => setCurrentPage(page)}
            />
          ) : (
            ""
          )}
        </div>

        <div
          key={`pending`}
          label={
            _("pending") + " (" + (totalTmpCollaborators + totalExpress) + ")"
          }
        >
          <div className={styles.tableContainer}>
            <table
              className={`${styles.table} unstriped`}
              onClick={() => setDropDownId(null)}
            >
              <thead>
                <tr>
                  <th></th>
                  <th width="30%" className={styles.nowrap}>
                    <span>{_("lastname") + " / " + _("firstname")}</span>
                    <span className={styles.sortBox}>
                      <i
                        className={`icon-ttp-arrow-down ${
                          sort.property === "lastName" &&
                          sort.dir === "asc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "lastName", dir: "asc" })
                        }
                      ></i>
                      <i
                        className={`icon-ttp-arrow-up ${
                          sort.property === "lastName" &&
                          sort.dir === "desc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "lastName", dir: "desc" })
                        }
                      ></i>
                    </span>
                  </th>
                  <th width="30%">
                    <span>Email</span>
                    <span className={styles.sortBox}>
                      <i
                        className={`icon-ttp-arrow-down ${
                          sort.property === "email" &&
                          sort.dir === "asc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "email", dir: "asc" })
                        }
                      ></i>
                      <i
                        className={`icon-ttp-arrow-up ${
                          sort.property === "email" &&
                          sort.dir === "desc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "email", dir: "desc" })
                        }
                      ></i>
                    </span>
                  </th>
                  <th width="25%">{_("phone_number")}</th>
                  <th width="25%">{_("role")}</th>
                  <th>{_("language")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {listExpress.isLoading || listTmpCollaborators.isLoading ? (
                  <tr>
                    <td colSpan={6} className={styles.centerCell}>
                      <Loader
                        style={{
                          height: "10px",
                        }}
                        color={"#B2BCC6"}
                      />
                    </td>
                  </tr>
                ) : (
                  renderPendingData()
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          key={`acitive`}
          label={
            _("rejected") +
            " (" +
            (totalRejectedExpress + totalRejectedTmpCollaborators) +
            ")"
          }
        >
          <div className={styles.tableContainer}>
            <table
              className={`${styles.table} unstriped`}
              onClick={() => setDropDownId(null)}
            >
              <thead>
                <tr>
                  <th></th>
                  <th width="30%" className={styles.nowrap}>
                    <span>{_("lastname") + " / " + _("firstname")}</span>
                    <span className={styles.sortBox}>
                      <i
                        className={`icon-ttp-arrow-down ${
                          sort.property === "lastName" &&
                          sort.dir === "asc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "lastName", dir: "asc" })
                        }
                      ></i>
                      <i
                        className={`icon-ttp-arrow-up ${
                          sort.property === "lastName" &&
                          sort.dir === "desc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "lastName", dir: "desc" })
                        }
                      ></i>
                    </span>
                  </th>
                  <th width="30%">
                    <span>Email</span>
                    <span className={styles.sortBox}>
                      <i
                        className={`icon-ttp-arrow-down ${
                          sort.property === "email" &&
                          sort.dir === "asc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "email", dir: "asc" })
                        }
                      ></i>
                      <i
                        className={`icon-ttp-arrow-up ${
                          sort.property === "email" &&
                          sort.dir === "desc" &&
                          styles.iconActive
                        }`}
                        onClick={() =>
                          setSort({ property: "email", dir: "desc" })
                        }
                      ></i>
                    </span>
                  </th>
                  <th width="25%">{_("phone_number")}</th>
                  <th width="25%">{_("role")}</th>
                  <th>{_("language")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>{renderRejectedData()}</tbody>
            </table>
          </div>
        </div>
      </Tabs>

      <ModalConfirm
        type="delete"
        isOpen={isOpenDeleteModal}
        onCancel={() => {
          setIsOpenDeleteModal(false);
        }}
        onConfirm={() => handleDeleteAction()}
        inProcess={deleting}
        title={_("delete_confirm")}
        text={_("text_delete_collaborator")}
        labelNo={_("no")}
        labelYes={_("yes")}
        labelError={_("error")}
      />

      {userRoleModal && (
        <UserRoleModal
          modalOpen={userRoleModal ? true : false}
          closeModal={() => {
            setUserRoleModal(null);
          }}
          firstName={userRoleModal.firstname ? userRoleModal.firstname : ""}
          lastName={userRoleModal.lastname ? userRoleModal.lastname : ""}
          email={userRoleModal.email ? userRoleModal.email : ""}
          userId={userRoleModal.ttp_user_id}
        />
      )}
    </>
  );
};

export default CollaboratorList;
